import React, { useEffect } from 'react';

/**
 * DayDetailModal Component
 * Shows detailed view of all events for a selected day
 */
const DayDetailModal = ({ date, events, onClose, onEventSponsor, centerName, theme, occasionData }) => {
  // Parse date for display
  const dateObj = new Date(date + 'T00:00:00');
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
  const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
  const dayNumber = dateObj.getDate();
  const year = dateObj.getFullYear();
  
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  // Styles
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      animation: 'fadeIn 0.2s'
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '12px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.3s',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
    },
    header: {
      padding: '24px',
      borderBottom: '1px solid #e2e8f0',
      position: 'relative'
    },
    closeButton: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      padding: '8px',
      borderRadius: '8px',
      transition: 'all 0.2s',
      outline: 'none'
    },
    closeButtonHover: {
      backgroundColor: '#f1f5f9',
      color: theme?.colors?.cardText || '#333'
    },
    dateTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: theme?.colors?.primary || '#0f172a',
      marginBottom: '4px'
    },
    dateSubtitle: {
      fontSize: '14px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    },
    centerName: {
      fontSize: '12px',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      marginTop: '4px'
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    },
    emptyIcon: {
      fontSize: '48px',
      marginBottom: '16px',
      opacity: 0.5
    },
    emptyText: {
      fontSize: '14px'
    },
    eventItem: {
      marginBottom: '16px',
      padding: '16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s'
    },
    eventItemHover: {
      backgroundColor: '#f1f5f9',
      borderColor: '#cbd5e0'
    },
    eventHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: '8px',
      gap: '12px'
    },
    eventTime: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
      fontWeight: '500',
      color: theme?.colors?.cardText || '#333',
      marginBottom: '4px'
    },
    eventTitle: {
      fontSize: '16px',
      fontWeight: '500',
      color: theme?.colors?.cardText || '#333',
      marginBottom: '4px',
      flex: 1
    },
    eventTypeBadge: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      color: 'white',
      marginBottom: '8px'
    },
    eventDescription: {
      fontSize: '13px',
      color: theme?.colors?.cardTextSecondary || '#64748b',
      lineHeight: '1.5',
      marginTop: '8px'
    },
    sponsorButton: {
      backgroundColor: '#fbbf24',
      color: '#000',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginTop: '12px'
    },
    sponsorButtonHover: {
      backgroundColor: '#f59e0b',
      transform: 'translateY(-2px)'
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    footer: {
      padding: '16px 24px',
      borderTop: '1px solid #e2e8f0',
      textAlign: 'center'
    },
    eventCount: {
      fontSize: '13px',
      color: theme?.colors?.cardTextSecondary || '#64748b'
    }
  };
  
  // Sort events by time
  const sortedEvents = [...events].sort((a, b) => {
    const timeA = a.start_time || '00:00';
    const timeB = b.start_time || '00:00';
    return timeA.localeCompare(timeB);
  });
  
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <button
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => Object.assign(e.target.style, styles.closeButtonHover)}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '';
              e.target.style.color = theme?.colors?.cardTextSecondary || '#64748b';
            }}
            aria-label="Close"
          >
            ✕
          </button>
          
          <div style={styles.dateTitle}>
            {dayName}, {monthName} {dayNumber}
          </div>
          <div style={styles.dateSubtitle}>
            {year} • {events.length} {events.length === 1 ? 'Event' : 'Events'}
            {occasionData?.occasions?.length > 0 && ` + ${occasionData.occasions.length} Islamic Occasion${occasionData.occasions.length > 1 ? 's' : ''}`}
          </div>
          {/* Hijri date badge */}
          {occasionData?.hijri && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              marginTop: '6px', padding: '3px 10px', borderRadius: '8px',
              fontSize: '12px', fontWeight: 500,
              background: '#f1f5f9',
              color: theme?.colors?.cardTextSecondary || '#64748b',
            }}>
              ☪ {occasionData.hijri.day} {occasionData.hijri.monthName} {occasionData.hijri.year} AH
            </div>
          )}
          <div style={styles.centerName}>
            {centerName}
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Islamic occasion cards (above events) */}
          {occasionData?.occasions?.map((occasion, i) => {
            const isMourning = occasion.type === 'mourning';
            const isJoyous = occasion.type === 'joyous';
            return (
              <div key={`modal-occasion-${i}`} style={{
                padding: '16px', borderRadius: '8px', marginBottom: '16px',
                background: isMourning
                  ? 'linear-gradient(135deg, #1a1a1a, #2d2d2d)'
                  : isJoyous
                  ? 'linear-gradient(135deg, #f0fdf4, #fefce8)'
                  : 'linear-gradient(135deg, #eff6ff, #f5f3ff)',
                border: isMourning ? '1px solid #333'
                  : isJoyous ? '1px solid #bbf7d0'
                  : '1px solid #bfdbfe',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{
                      fontSize: '16px', fontWeight: 600, marginBottom: '4px',
                      color: isMourning ? '#f0f0f0' : (theme?.colors?.cardText || '#333'),
                    }}>
                      {isMourning ? '🏴' : isJoyous ? '🎉' : '✨'} {occasion.nameEn}
                    </div>
                    {occasion.nameAr && (
                      <div style={{
                        fontSize: '15px', opacity: 0.7, marginBottom: '6px',
                        direction: 'rtl', textAlign: 'right',
                        fontFamily: "'Traditional Arabic', 'Noto Naskh Arabic', serif",
                        color: isMourning ? '#ccc' : 'inherit',
                      }}>
                        {occasion.nameAr}
                      </div>
                    )}
                  </div>
                  <span style={{
                    fontSize: '11px', padding: '2px 8px', borderRadius: '8px', fontWeight: 500,
                    background: isMourning ? '#333' : isJoyous ? '#dcfce7' : '#dbeafe',
                    color: isMourning ? '#aaa' : isJoyous ? '#166534' : '#1e40af',
                  }}>
                    {occasion.type === 'mourning' ? 'Mourning' : occasion.type === 'joyous' ? 'Joyous' : 'Significant'}
                  </span>
                </div>
                {occasion.significance && (
                  <div style={{
                    fontSize: '13px', marginBottom: '10px', lineHeight: 1.5,
                    color: isMourning ? '#aaa' : (theme?.colors?.cardTextSecondary || '#475569'),
                  }}>
                    {occasion.significance}
                  </div>
                )}
                {occasion.amaalSummary && (
                  <div style={{
                    fontSize: '12px', padding: '8px 12px', borderRadius: '6px', marginBottom: '8px',
                    background: isMourning ? '#333' : isJoyous ? '#dcfce7' : '#dbeafe',
                    color: isMourning ? '#bbb' : isJoyous ? '#166534' : '#1e40af',
                  }}>
                    <strong>Recommended:</strong> {occasion.amaalSummary}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {occasion.amaalUrlPrimary && (
                    <a href={occasion.amaalUrlPrimary} target="_blank" rel="noopener noreferrer" style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '6px 14px', borderRadius: '6px', fontSize: '12px',
                      fontWeight: 500, textDecoration: 'none',
                      background: isMourning ? '#555' : isJoyous ? (theme?.colors?.primary || '#166534') : '#1e40af',
                      color: isMourning ? '#ddd' : '#fff',
                    }}>
                      🤲 Amaal on Duas.org
                    </a>
                  )}
                  {occasion.amaalUrlSecondary && (
                    <a href={occasion.amaalUrlSecondary} target="_blank" rel="noopener noreferrer" style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '6px 14px', borderRadius: '6px', fontSize: '12px',
                      fontWeight: 500, textDecoration: 'none',
                      background: 'transparent',
                      border: `1px solid ${isMourning ? '#666' : isJoyous ? (theme?.colors?.primary || '#166534') : '#1e40af'}`,
                      color: isMourning ? '#aaa' : isJoyous ? (theme?.colors?.primary || '#166534') : '#1e40af',
                    }}>
                      🎧 DuaPlayer
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          {sortedEvents.length === 0 && (!occasionData?.occasions || occasionData.occasions.length === 0) ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📅</div>
              <div style={styles.emptyText}>
                No events scheduled for this day
              </div>
            </div>
          ) : sortedEvents.length === 0 ? null : (
            sortedEvents.map(event => (
              <div
                key={event.id}
                style={styles.eventItem}
                onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.eventItemHover)}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <div style={styles.eventHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.eventTime}>
                      🕐 {event.displayTime}
                    </div>
                    <div style={styles.eventTitle}>{event.title}</div>
                    <span
                      style={{
                        ...styles.eventTypeBadge,
                        backgroundColor: event.color,
                        color: event.color === '#86efac' || event.color === '#fbbf24' ? '#000' : '#fff'
                      }}
                    >
                      {event.typeLabel}
                    </span>
                  </div>
                </div>
                
                {event.metadata?.sponsor_name && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '3px 10px', borderRadius: '6px', marginTop: '6px',
                    backgroundColor: `${theme?.colors?.primary || '#0f172a'}10`,
                    border: `1px solid ${theme?.colors?.primary || '#0f172a'}20`,
                    fontSize: '12px', fontWeight: '600',
                    color: theme?.colors?.primary || '#0f172a',
                  }}>
                    Sponsored by {event.metadata.sponsor_name}
                  </div>
                )}

                {event.description && (
                  <div style={styles.eventDescription}>
                    {event.description}
                  </div>
                )}

                {event.canSponsor && onEventSponsor && (
                  <button
                    style={styles.sponsorButton}
                    onClick={() => onEventSponsor(event)}
                    onMouseEnter={(e) => Object.assign(e.target.style, styles.sponsorButtonHover)}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#fbbf24';
                      e.target.style.transform = '';
                    }}
                  >
                    Sponsor This Event
                  </button>
                )}
                
                {event.isPast && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: theme?.colors?.cardTextSecondary || '#64748b',
                    marginTop: '8px',
                    fontStyle: 'italic'
                  }}>
                    This event has already occurred
                  </div>
                )}
              </div>
            ))
          )}
        </div>
        
        {/* Footer */}
        {sortedEvents.length > 0 && (
          <div style={styles.footer}>
            <div style={styles.eventCount}>
              Showing all {sortedEvents.length} {sortedEvents.length === 1 ? 'event' : 'events'} for this day
            </div>
          </div>
        )}
      </div>
      
      {/* Add animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default DayDetailModal;